import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import PipelineService, { pipelineErrorMessage } from "../../services/pipeline.service";
import { IPagerDutyRule, IPagerDutyRuleUpdate } from "../../types/api/pipeline.type";
import useNotifications from "../../components/NotificationProvider";
import { CONTROLLABLE_SERVICES } from "./common";

const ruleKey = (r: IPagerDutyRule) => `${r.service}/${r.condition}`;

const UNIT_SUFFIX: Record<string, string> = { seconds: "s", fraction: "%", count: "tasks", none: "" };

/**
 * "fraction" thresholds are stored as 0–1 but shown and typed as a percent, the
 * same as the rule's Current value ("15.0%") and the alert text. Rounded so 0.1
 * shows as 10, not 10.000000000000002.
 */
const toDisplay = (r: IPagerDutyRule, value: number | null) => {
  if (value === null || value === undefined) return "";
  return String(r.unit === "fraction" ? +(value * 100).toFixed(4) : value);
};

const fromDisplay = (r: IPagerDutyRule, text: string) => {
  const n = Number(text);
  return r.unit === "fraction" ? n / 100 : n;
};

const SERVICE_ORDER = [...CONTROLLABLE_SERVICES, "maintenance"];
const serviceRank = (s: string) => {
  const i = SERVICE_ORDER.indexOf(s);
  return i === -1 ? SERVICE_ORDER.length : i;
};

const PagerDutyTab = ({ envId }: { envId: string }) => {
  const { setNotification } = useNotifications();
  const [rules, setRules] = useState<IPagerDutyRule[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await PipelineService.getPagerDutyRules(envId);
      setRules(data.rules ?? []);
      setError(null);
    } catch (e) {
      setError(pipelineErrorMessage(e, "Could not load PagerDuty rules"));
    } finally {
      setLoading(false);
    }
  }, [envId]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (rule: IPagerDutyRule, body: IPagerDutyRuleUpdate, success: string) => {
    const key = ruleKey(rule);
    setSaving(key);
    try {
      await PipelineService.updatePagerDutyRule(envId, rule.service, rule.condition, body);
      setDrafts((d) => {
        const next = { ...d };
        delete next[key];
        return next;
      });
      setNotification({ message: success, open: true });
      await load();
    } catch (e) {
      // 400s carry the validation reason (e.g. "fraction must be <= 1").
      setNotification({ message: pipelineErrorMessage(e), open: true, type: "error" });
    } finally {
      setSaving(null);
    }
  };

  const grouped = rules.reduce<Record<string, IPagerDutyRule[]>>((acc, r) => {
    (acc[r.service] = acc[r.service] || []).push(r);
    return acc;
  }, {});
  const services = Object.keys(grouped).sort((a, b) => serviceRank(a) - serviceRank(b));

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Changes apply on the pipeline's next health check (within about a minute). Disabling a rule resolves any
          incident it has open.
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && rules.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="pagerduty rules">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Condition</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Severity</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Current</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Enabled</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Threshold</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((service) => (
                <Fragment key={service}>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ backgroundColor: "rgba(255,255,255,0.04)", fontWeight: "bold" }}>
                      {service}
                    </TableCell>
                  </TableRow>
                  {grouped[service].map((rule) => {
                    const key = ruleKey(rule);
                    const hasThreshold = rule.unit !== "none";
                    const draft = drafts[key];
                    const shown = draft ?? toDisplay(rule, rule.threshold);
                    const dirty = draft !== undefined && draft !== toDisplay(rule, rule.threshold);
                    const isDefault = rule.threshold === rule.default_threshold;
                    const busy = saving === key;
                    return (
                      <TableRow key={key} hover>
                        <TableCell sx={{ pl: 4 }}>{rule.label}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={rule.severity}
                            color={rule.severity === "critical" ? "error" : "warning"}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                          {rule.current_value ?? "unknown"}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={rule.enabled}
                            disabled={busy}
                            onChange={(e) =>
                              update(
                                rule,
                                { enabled: e.target.checked },
                                `${rule.service} · ${rule.label} ${e.target.checked ? "enabled" : "disabled"}`
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {hasThreshold ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                size="small"
                                type="number"
                                value={shown}
                                onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                                inputProps={{ step: "any", min: 0 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">{UNIT_SUFFIX[rule.unit]}</InputAdornment>
                                  ),
                                }}
                                helperText={`default ${toDisplay(rule, rule.default_threshold)} ${
                                  UNIT_SUFFIX[rule.unit]
                                }`}
                                sx={{ width: 170 }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!dirty || busy || shown.trim() === "" || Number.isNaN(Number(shown))}
                                onClick={() =>
                                  update(
                                    rule,
                                    { threshold: fromDisplay(rule, shown) },
                                    `${rule.service} · ${rule.label} threshold saved`
                                  )
                                }
                              >
                                Save
                              </Button>
                              <Tooltip title="Go back to the built-in default (and follow it if it changes)">
                                <span>
                                  <Button
                                    size="small"
                                    disabled={isDefault || busy}
                                    onClick={() =>
                                      update(
                                        rule,
                                        { reset_threshold: true },
                                        `${rule.service} · ${rule.label} reset to default`
                                      )
                                    }
                                  >
                                    Reset
                                  </Button>
                                </span>
                              </Tooltip>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              — (on/off only)
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </Fragment>
              ))}
              {!loading && rules.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={5}>No rules defined</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PagerDutyTab;
